import { CrossCircledIcon } from "@radix-ui/react-icons";
import {
  useLayoutEffect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { isAxiosError } from "axios";

import { ButtonLink } from "@/components/ui/button";
import { useIsAuthenticated } from "@/lib/hooks";

const isModuleNotFoundError = (error: string): boolean =>
  /Failed to fetch dynamically imported module/.test(error);

export function ErrorPage({
  title,
  description,
  reset,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  reset?: () => void;
}) {
  const isAuthed = useIsAuthenticated();

  return (
    <div className="flex items-center justify-center text-red-11 dark:text-reddark-11 flex-1 min-h-screen">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <CrossCircledIcon className="w-8 h-8" />
          <div className="flex flex-col">
            <div className="text-xl">{title}</div>
            <div>{description}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {isAuthed ? (
            <ButtonLink className="flex-1" to="/" onClick={reset}>
              Go to Dashboard
            </ButtonLink>
          ) : (
            <ButtonLink className="flex-1" to="/login">
              Log in
            </ButtonLink>
          )}
        </div>
      </div>
    </div>
  );
}

export function RedirectErrorPage({
  error,
  reset,
}: {
  error: Error;
  reset?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldRedirect =
    isAxiosError(error) && [401, 403].includes(error.status || 0);
  const redirectParam =
    location.search.redirect ||
    (location.search
      ? `${location.pathname}?${Object.entries(location.search)
          .map(([k, v]) => `${k}=${v as string}`)
          .join("&")}`
      : location.pathname);

  useLayoutEffect(() => {
    if (shouldRedirect) {
      navigate({
        to: "/login",
        search: { redirect: redirectParam },
      }).catch(console.error);
    }
  }, [shouldRedirect, redirectParam, navigate]);

  if (shouldRedirect) {
    return null;
  }

  if (isModuleNotFoundError(error.message)) {
    return <div />;
  }

  return (
    <ErrorPage
      title="Oh no! Something went wrong!"
      description={error.message}
      reset={reset}
    />
  );
}
